import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom } from 'rxjs';
import {
  AddTagsDialogComponent,
  AddTagsDialogData,
  AddTagsDialogResult,
} from './add-tags-dialog/add-tags-dialog.component';
import { Opportunity, SalesOpportunitiesService } from '@vendasta/sales-opportunities';
import { AccountGroup, AccountGroupApiService, ProjectionFilter } from '@galaxy/account-group';

interface Tag {
  name: string;
  accountTag?: boolean;
}

@Component({
  selector: 'sales-ui-opportunity-tags',
  templateUrl: './opportunity-tags.component.html',
  styleUrls: ['./opportunity-tags.component.scss'],
})
export class OpportunityTagsComponent {
  @Input() set opportunity(opp: Opportunity) {
    this.opportunity$$.next(opp);
  }
  @Input() loading: boolean;
  @Output() tagsChanged: EventEmitter<string[]> = new EventEmitter();

  private readonly opportunity$$ = new BehaviorSubject<Opportunity>(null);
  private readonly accountGroupInfo$: Observable<AccountGroup>;
  private readonly accountTags$: Observable<Tag[]>;
  private readonly opportunityTags$: Observable<Tag[]>;
  private readonly availableTags$: Observable<string[]>;
  readonly opportunity$ = this.opportunity$$.asObservable();
  readonly tags$: Observable<Tag[]>;
  readonly hasTags$: Observable<boolean>;

  constructor(
    private readonly dialog: MatDialog,
    private readonly accountGroupService: AccountGroupApiService,
    private readonly salesOpportunitiesService: SalesOpportunitiesService,
  ) {
    this.accountGroupInfo$ = this.opportunity$.pipe(
      filter((opp) => Boolean(opp)),
      distinctUntilChanged((a, b) => a.accountGroupId === b.accountGroupId),
      switchMap((opp) => this.getAccountGroup(opp.accountGroupId)),
      shareReplay(1),
    );
    this.accountTags$ = this.accountGroupInfo$.pipe(
      map((accountGroup) => this.getAccountGroupTags(accountGroup)),
      map((tags) => tags.map((tag) => ({ name: tag, accountTag: true } as Tag))),
    );
    this.opportunityTags$ = this.opportunity$.pipe(
      map((opp) => opp?.tags?.map((tag) => ({ name: tag, accountTag: false } as Tag)) || []),
    );
    this.tags$ = combineLatest([this.opportunityTags$, this.accountTags$]).pipe(
      map(([oppTags, accountTags]) => oppTags.concat(accountTags)),
      shareReplay(1),
    );
    this.hasTags$ = this.tags$.pipe(map((tags) => tags?.length > 0));
    this.availableTags$ = this.accountGroupInfo$.pipe(
      switchMap((accountGroup) => this.getSuggestedTags(accountGroup)),
      shareReplay(1),
    );
  }

  private getAccountGroup(accountGroupId: string): Observable<AccountGroup> {
    return this.accountGroupService.get(
      accountGroupId,
      new ProjectionFilter({ accountGroupExternalIdentifiers: true }),
    );
  }

  private getAccountGroupTags(accountGroup: AccountGroup): string[] {
    return accountGroup?.externalIdentifiers?.tags ?? [];
  }

  private getSuggestedTags(accountGroup: AccountGroup): Observable<string[]> {
    return this.salesOpportunitiesService.getTags(
      accountGroup?.externalIdentifiers?.partnerId,
      accountGroup?.externalIdentifiers?.marketId,
    );
  }

  async openEditTagsDialog(): Promise<void> {
    this.dialog.closeAll();
    const opp = this.opportunity$$.getValue();
    const availableTags = await firstValueFrom(this.availableTags$);
    const data: AddTagsDialogData = {
      opportunityId: opp.opportunityId,
      accountGroupId: opp.accountGroupId,
      opportunityTags: opp.tags,
      availableTags: availableTags,
    };
    const config: MatDialogConfig<AddTagsDialogData> = {
      minWidth: '40vw',
      maxWidth: '100vw',
      width: '600px',
      data,
    };
    const editTagsDialogRef: MatDialogRef<AddTagsDialogComponent, AddTagsDialogResult> = this.dialog.open(
      AddTagsDialogComponent,
      config,
    );
    editTagsDialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((res) => res !== null),
      )
      .subscribe((tags) => this.tagsChanged.emit(tags));
  }
}
